import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import StudentFilterBar from '../components/StudentDashboard/StudentDashFilterBarContainer';
import PositionGrid from '../components/StudentDashboard/PositionGrid';
import { useDebounce } from '../hooks/useDebounce';
import PositionModal from '../components/PositionCard/PositionModal';
import PaginationControls from '../components/StudentDashboard/PaginationControls';
import '../components/StudentDashboard/StudentDashboard.css';

export default function StudentDashboard() {
  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [filters, setFilters] = useState({
    title: '',
    description: '',
    institutions: [],
    fields: [],
    start_semester: '',
    start_year: '',
    city: '',
    country: '',
    native: false,
    fully_funded: '',
    funding_source: '',
    lab_size: '',
    publication_rate: '',
    degree_level: '',
  });
  const [sortBy, setSortBy] = useState("relevance");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Debounce text filters to reduce API calls
  const debouncedFilters = useDebounce(filters, 500);

  const pageSize = 12;

  const fetchPositions = useCallback(async () => {
    setIsLoading(true);
    try {
      const stored = localStorage.getItem("user");
      const userData = stored ? JSON.parse(stored) : {};
      const token = userData.token;
      const query = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        title: debouncedFilters.title,
        description: debouncedFilters.description,
        start_semester: debouncedFilters.start_semester,
        start_year: debouncedFilters.start_year,
        city: debouncedFilters.city,
        country: debouncedFilters.country,
        native: debouncedFilters.native,
        fully_funded: debouncedFilters.fully_funded,
        funding_source: debouncedFilters.funding_source,
        lab_size: debouncedFilters.lab_size,
        publication_rate: debouncedFilters.publication_rate,
        degree_level: debouncedFilters.degree_level,
        sortBy: sortBy,
      });

      // Handle array filters
      debouncedFilters.institutions.forEach(inst => query.append('institutions', inst));
      debouncedFilters.fields.forEach(field => query.append('fields', field));

      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/positions?${query.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setPositions(response.data.positions);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching positions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedFilters, currentPage, sortBy]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1); // Reset to first page when sort changes
  };

  const handlePositionView = (position) => {
    setSelectedPosition(position);
  };

  const handleCloseModal = () => {
    setSelectedPosition(null);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="student-dashboard-root">
      <div className="dashboard-container-top">
        <div className="top-filter-section">
          <StudentFilterBar onFilterChange={handleFilterChange} initialFilters={filters} />
        </div>
      </div>

      <div className="dashboard-container">
        {/* Main Content */}
        <div className="main-content">
          <div className="std-dash-header">
            <div className="std-header-top">
              <div className="std-title-section">
                <h1 className="std-dash-title">Position Pool</h1>
                <p className="std-dash-subtitle">
                  Discover academic positions that match your interests and qualifications
                </p>
              </div>

              {/* Sort Dropdown */}
              <div className="std-sort-section">
                <label htmlFor="sort-select" className="std-sort-label">Sort by:</label>
                <select
                  id="sort-select"
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="std-sort-select"
                >
                  <option value="relevance">Relevance</option>
                  <option value="title-asc">Title (A-Z)</option>
                  <option value="title-desc">Title (Z-A)</option>
                  <option value="recent">Recently Added</option>
                </select>
              </div>
            </div>

            {/* Result Count and Active Filters */}
            <div className="std-header-bottom">
              <div className="std-res-count">
                {isLoading ? (
                  <span className="result-count-loading">Searching...</span>
                ) : (
                  <span className="std-res-count-text">
                    {totalCount} {totalCount === 1 ? 'position' : 'positions'} found
                  </span>
                )}
              </div>

              {/* Active Filter Badges */}
              {(filters.title || filters.description || filters.institutions.length > 0 ||
                filters.fields.length > 0 || filters.city || filters.country ||
                filters.start_semester || filters.start_year) && (
                  <div className="std-active-filters-section">
                    <span className="active-filters-label">Active filters:</span>
                    <div className="std-active-filters">
                      {filters.title && (
                        <span className="std-filter-badge">
                          Title: {filters.title}
                          <button
                            onClick={() => setFilters({ ...filters, title: "" })}
                            className="std-filter-badge-remove"
                            aria-label="Remove title filter"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.description && (
                        <span className="std-filter-badge">
                          Description: {filters.description}
                          <button
                            onClick={() => setFilters({ ...filters, description: "" })}
                            className="std-filter-badge-remove"
                            aria-label="Remove description filter"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.city && (
                        <span className="std-filter-badge">
                          City: {filters.city}
                          <button
                            onClick={() => setFilters({ ...filters, city: "" })}
                            className="std-filter-badge-remove"
                            aria-label="Remove city filter"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.country && (
                        <span className="std-filter-badge">
                          Country: {filters.country}
                          <button
                            onClick={() => setFilters({ ...filters, country: "" })}
                            className="std-filter-badge-remove"
                            aria-label="Remove country filter"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.start_semester && (
                        <span className="std-filter-badge">
                          Semester: {filters.start_semester}
                          <button
                            onClick={() => setFilters({ ...filters, start_semester: "" })}
                            className="std-filter-badge-remove"
                            aria-label="Remove semester filter"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.start_year && (
                        <span className="std-filter-badge">
                          Year: {filters.start_year}
                          <button
                            onClick={() => setFilters({ ...filters, start_year: "" })}
                            className="std-filter-badge-remove"
                            aria-label="Remove year filter"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.institutions.map((inst) => (
                        <span key={inst} className="std-filter-badge">
                          Institution: {inst}
                          <button
                            onClick={() =>
                              setFilters({
                                ...filters,
                                institutions: filters.institutions.filter((i) => i !== inst),
                              })
                            }
                            className="std-filter-badge-remove"
                            aria-label={`Remove ${inst} filter`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {filters.fields.map((field) => (
                        <span key={field} className="std-filter-badge">
                          Field: {field}
                          <button
                            onClick={() =>
                              setFilters({
                                ...filters,
                                fields: filters.fields.filter((f) => f !== field),
                              })
                            }
                            className="std-filter-badge-remove"
                            aria-label={`Remove ${field} filter`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {filters.fully_funded && (
                        <span className="std-filter-badge">
                          Funding: {filters.fully_funded}
                          <button
                            onClick={() => setFilters({ ...filters, fully_funded: "" })}
                            className="std-filter-badge-remove"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.funding_source && (
                        <span className="std-filter-badge">
                          Source: {filters.funding_source}
                          <button
                            onClick={() => setFilters({ ...filters, funding_source: "" })}
                            className="std-filter-badge-remove"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.lab_size && (
                        <span className="std-filter-badge">
                          Lab Size: {filters.lab_size}
                          <button
                            onClick={() => setFilters({ ...filters, lab_size: "" })}
                            className="std-filter-badge-remove"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.publication_rate && (
                        <span className="std-filter-badge">
                          Pub Rate: {filters.publication_rate}
                          <button
                            onClick={() => setFilters({ ...filters, publication_rate: "" })}
                            className="std-filter-badge-remove"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.degree_level && (
                        <span className="std-filter-badge">
                          Degree: {filters.degree_level}
                          <button
                            onClick={() => setFilters({ ...filters, degree_level: "" })}
                            className="std-filter-badge-remove"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.native && (
                        <span className="std-filter-badge">
                          NeuHawk Native
                          <button
                            onClick={() => setFilters({ ...filters, native: false })}
                            className="std-filter-badge-remove"
                            aria-label="Remove native filter"
                          >
                            ×
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>

          {isLoading ? (
            <div className="std-loading-container">
              <div className="std-loading-spinner"></div>
              <p className="std-loading-text">Loading positions...</p>
            </div>
          ) : (
            <>
              <PositionGrid
                positions={positions}
                onPositionView={handlePositionView}
              />

              {positions.length > 0 && (
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Position Detail Modal */}
      <PositionModal
        position={selectedPosition}
        isOpen={!!selectedPosition}
        onClose={handleCloseModal}
      />
    </div>
  );
}
